import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useFeature } from "../context/FeatureContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  CalendarDays, ChevronLeft, ChevronRight, Check, Clock,
  Sparkles, Thermometer, AlertTriangle, Camera, User,
  Briefcase, Info, CheckCircle2, ChefHat
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GIORNI_SETTIMANA = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const CalendarioAttivita = () => {
  const { getAuthHeaders, user, localeCorrente, isAdmin } = useAuth();
  const { hasFeature } = useFeature();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("giorno"); // giorno | settimana
  
  // Data - aggiunto gestione + preparazioni
  const [attivitaGiorno, setAttivitaGiorno] = useState({ pulizie: [], gestione: [], temperature: [], preparazioni: [], preparazioni_spontanee: [], stats: {} });
  const [weekData, setWeekData] = useState([]);
  
  // Temperature dialog
  const [tempDialogOpen, setTempDialogOpen] = useState(false);
  const [selectedFrigo, setSelectedFrigo] = useState(null);
  const [tempForm, setTempForm] = useState({ temperatura: "" });
  const [tempSaving, setTempSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [statoOperatore, setStatoOperatore] = useState(null);
  const [giornoLavorativo, setGiornoLavorativo] = useState(null);

  // Fetch stato operatore al cambio locale
  useEffect(() => {
    if (localeCorrente?.id) {
      fetchStatoOperatore();
    }
  }, [localeCorrente?.id]);

  // Fetch attività quando cambia data, viewMode o giornoLavorativo
  useEffect(() => {
    if (localeCorrente?.id && giornoLavorativo !== null) {
      if (viewMode === "giorno") {
        fetchAttivitaGiorno(formatDate(currentDate));
      } else {
        fetchSettimana();
      }
    }
  }, [localeCorrente?.id, currentDate, viewMode, giornoLavorativo]);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const fetchStatoOperatore = async () => {
    try {
      const res = await axios.get(
        `${API}/stato-turno-operatore?locale_id=${localeCorrente.id}`,
        { headers: getAuthHeaders() }
      );
      setStatoOperatore(res.data);
      const gl = res.data.giorno_lavorativo;
      if (gl) {
        setGiornoLavorativo(gl);
        const [y, m, d] = gl.split('-').map(Number);
        setCurrentDate(new Date(y, m - 1, d));
      } else {
        const fallback = formatDate(new Date());
        setGiornoLavorativo(fallback);
      }
    } catch (error) {
      console.error("Errore recupero stato operatore:", error);
      const fallback = formatDate(new Date());
      setGiornoLavorativo(fallback);
    }
  };

  const fetchAttivitaGiorno = async (data) => {
    if (!localeCorrente?.id) return;
    
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/calendario-attivita?locale_id=${localeCorrente.id}&data=${data}`,
        { headers: getAuthHeaders() }
      );
      setAttivitaGiorno(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettimana = async () => {
    if (!localeCorrente?.id) return;
    
    try {
      setLoading(true);
      // Calcola la settimana corrente
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
      startOfWeek.setDate(diff);
      
      const promises = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        promises.push(
          axios.get(
            `${API}/calendario-attivita?locale_id=${localeCorrente.id}&data=${formatDate(d)}`,
            { headers: getAuthHeaders() }
          ).then(res => ({ date: formatDate(d), day: d, ...res.data }))
        );
      }
      
      const results = await Promise.all(promises);
      setWeekData(results);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (delta) => {
    const newDate = new Date(currentDate);
    if (viewMode === "giorno") {
      newDate.setDate(newDate.getDate() + delta);
    } else {
      newDate.setDate(newDate.getDate() + (delta * 7));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    if (giornoLavorativo) {
      const [y, m, d] = giornoLavorativo.split('-').map(Number);
      setCurrentDate(new Date(y, m - 1, d));
    } else {
      setCurrentDate(new Date());
    }
  };

  const handleCheckPulizia = async (task) => {
    try {
      await axios.post(`${API}/registrazioni-pulizia`, {
        locale_id: localeCorrente.id,
        task_id: task.id,
        data: formatDate(currentDate),
        completata: !task.completata,
        operatore_id: user?.id,
        operatore_nome: user?.nome || user?.email || user?.telefono
      }, { headers: getAuthHeaders() });
      
      toast.success(task.completata ? "Pulizia annullata" : "Pulizia completata!");
      fetchAttivitaGiorno(formatDate(currentDate));
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  // Preparazioni - dialog per inserire quantità
  const [prepDialogOpen, setPrepDialogOpen] = useState(false);
  const [selectedPrep, setSelectedPrep] = useState(null);
  const [prepQuantita, setPrepQuantita] = useState("");
  const [prepSaving, setPrepSaving] = useState(false);

  const openPrepDialog = (task) => {
    setSelectedPrep(task);
    setPrepQuantita(task.quantita_preparata?.toString() || task.quantita_target?.toString() || "");
    setPrepDialogOpen(true);
  };

  const handlePrepSubmit = async (e) => {
    e?.preventDefault?.();
    if (!selectedPrep) return;
    const q = parseFloat(prepQuantita);
    if (isNaN(q) || q < 0) {
      toast.error("Inserisci una quantità valida");
      return;
    }
    // Se esiste una somma lotti diversa da q → chiedi conferma override
    const sommaLotti = parseFloat(selectedPrep.quantita_lotti || 0);
    const ha_lotti = (selectedPrep.lotto_ids || []).length > 0 && sommaLotti > 0;
    if (ha_lotti && Math.abs(sommaLotti - q) > 0.01) {
      const confermato = window.confirm(
        `Sei sicuro di aver preparato ${q} ${selectedPrep.unita_misura || "kg"}?\n\n` +
        `I lotti registrati oggi per questa ricetta totalizzano ${sommaLotti} ${selectedPrep.unita_misura || "kg"}.\n\n` +
        `Confermando, il valore manuale "${q}" sovrascriverà quello dei lotti.`
      );
      if (!confermato) return;
      // Manda override
      await savePrepRegistrazione(q, true);
    } else {
      await savePrepRegistrazione(q, false);
    }
  };

  const savePrepRegistrazione = async (q, isOverride) => {
    setPrepSaving(true);
    try {
      const today = formatDate(currentDate);
      const operatoreNome = user?.nome || user?.email || user?.telefono || "Operatore";
      await axios.post(`${API}/registrazioni-gestione`, {
        locale_id: localeCorrente.id,
        task_id: selectedPrep.id,
        data: today,
        completata: q >= (selectedPrep.quantita_target || 0),
        operatore_id: user?.id,
        operatore_nome: operatoreNome,
        quantita_preparata: q,
        manuale_override: isOverride,
      }, { headers: getAuthHeaders() });
      toast.success(isOverride ? "Override manuale confermato" : "Preparazione registrata!");
      setPrepDialogOpen(false);
      fetchAttivitaGiorno(today);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setPrepSaving(false);
    }
  };

  // Gestione - handler per task di gestione operativi
  const handleCheckGestione = async (task) => {
    // Solo task operative possono essere completate
    if (task.tipo === "informativa") return;
    
    try {
      await axios.post(`${API}/registrazioni-gestione`, {
        locale_id: localeCorrente.id,
        task_id: task.id,
        data: formatDate(currentDate),
        completata: !task.completata,
        operatore_id: user?.id,
        operatore_nome: user?.nome || user?.email || user?.telefono
      }, { headers: getAuthHeaders() });
      
      toast.success(task.completata ? "Task annullato" : "Task completato!");
      fetchAttivitaGiorno(formatDate(currentDate));
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  // Temperature
  const openTempDialog = (frigo) => {
    setSelectedFrigo(frigo);
    setTempForm({ temperatura: frigo.temperatura?.toString() || "" });
    setTempDialogOpen(true);
  };

  const handleTempSubmit = async (e) => {
    e.preventDefault();
    if (!tempForm.temperatura) {
      toast.error("Inserisci la temperatura");
      return;
    }

    setTempSaving(true);
    try {
      await axios.post(`${API}/temperature`, {
        locale_id: localeCorrente.id,
        frigorifero_id: selectedFrigo.id,
        temperatura: parseFloat(tempForm.temperatura),
        data: formatDate(currentDate),
        ora: new Date().toTimeString().slice(0, 5),
        operatore: user?.nome || user?.email || user?.telefono
      }, { headers: getAuthHeaders() });
      
      toast.success("Temperatura registrata!");
      setTempDialogOpen(false);
      fetchAttivitaGiorno(formatDate(currentDate));
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setTempSaving(false);
    }
  };

  const handleOcrTemp = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setOcrLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tipo", "temperatura");

      const res = await axios.post(`${API}/ocr/temperatura`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });

      if (res.data.temperatura !== null) {
        setTempForm({ temperatura: res.data.temperatura.toString() });
        toast.success(`Temperatura rilevata: ${res.data.temperatura}°C`);
      } else {
        toast.error("Temperatura non rilevata dalla foto");
      }
    } catch (error) {
      toast.error("Errore nell'analisi della foto");
    } finally {
      setOcrLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Helpers
  const isToday = (date) => {
    if (giornoLavorativo) return formatDate(date) === giornoLavorativo;
    return formatDate(date) === formatDate(new Date());
  };

  // Operatori: possono interagire solo se in turno attivo nel giorno lavorativo corrente.
  // ECCEZIONE: se la società NON ha il pacchetto "presenze" (Gestione Personale),
  // l'operatore può completare le attività in qualsiasi momento del giorno corrente senza timbrare.
  const isViewingGiornoLavorativo = giornoLavorativo && formatDate(currentDate) === giornoLavorativo;
  const hasPresenteFeature = hasFeature("presenze");
  const canInteract = isAdmin
    || (isViewingGiornoLavorativo && !hasPresenteFeature)
    || (isViewingGiornoLavorativo && hasPresenteFeature && statoOperatore?.in_turno === true);

  const getDateDisplay = () => {
    if (viewMode === "giorno") {
      const options = { weekday: 'long', day: 'numeric', month: 'long' };
      return currentDate.toLocaleDateString('it-IT', options);
    } else {
      // Settimana
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${MESI[endOfWeek.getMonth()]}`;
    }
  };

  if (loading && !attivitaGiorno.pulizie?.length && !weekData.length) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="calendario-attivita-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Calendario
            </h1>
            <p className="page-subtitle">Pulizie e controllo temperature</p>
          </div>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-between bg-card rounded-2xl p-3 border border-border shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateDate(-1)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <div className="text-center">
            <p className="text-foreground font-bold capitalize">{getDateDisplay()}</p>
            {!isToday(currentDate) && (
              <button onClick={goToToday} className="text-primary text-xs mt-1 hover:underline">
                Vai ad oggi
              </button>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateDate(1)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* View Mode Toggle */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
          <TabsList className="tabs-pill-list">
            <TabsTrigger value="giorno">Giorno</TabsTrigger>
            <TabsTrigger value="settimana">Settimana</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Vista Giorno */}
      {viewMode === "giorno" && (
        <div className="space-y-6">
          {/* Avviso stato turno per operatori */}
          {!isAdmin && !canInteract && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-sm" data-testid="turno-warning">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {!isViewingGiornoLavorativo
                  ? "Puoi completare le attività solo per la giornata lavorativa corrente"
                  : statoOperatore?.motivo === "nessuna_entrata"
                    ? "Timbra l'entrata per poter completare le attività"
                    : statoOperatore?.motivo === "uscita_timbrata"
                      ? "Timbra l'entrata del prossimo turno per riattivare le attività"
                      : "Non puoi completare le attività al momento"}
              </span>
            </div>
          )}          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card rounded-2xl p-4 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-foreground/60 text-sm">Pulizie</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {attivitaGiorno.stats?.pulizie_completate || 0}
                <span className="text-foreground/40">/{attivitaGiorno.stats?.pulizie_totali || 0}</span>
              </p>
            </div>
            <div className="bg-card rounded-2xl p-4 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="h-4 w-4 text-orange-500" />
                <span className="text-foreground/60 text-sm">Gestione</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {attivitaGiorno.stats?.gestione_completate || 0}
                <span className="text-foreground/40">/{attivitaGiorno.stats?.gestione_totali || 0}</span>
              </p>
            </div>
            <div className="bg-card rounded-2xl p-4 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <ChefHat className="h-4 w-4 text-teal-500" />
                <span className="text-foreground/60 text-sm">Prep.</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {attivitaGiorno.stats?.preparazioni_completate || 0}
                <span className="text-foreground/40">/{attivitaGiorno.stats?.preparazioni_totali || 0}</span>
              </p>
            </div>
            <div className="bg-card rounded-2xl p-4 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Thermometer className="h-4 w-4 text-blue-500" />
                <span className="text-foreground/60 text-sm">Temp.</span>
              </div>
              <p className="text-2xl font-bold text-foreground">
                {attivitaGiorno.stats?.temperature_completate || 0}
                <span className="text-foreground/40">/{attivitaGiorno.stats?.temperature_totali || 0}</span>
              </p>
            </div>
          </div>

          {/* Pulizie Section */}
          <div className="space-y-3">
            <h2 className="text-foreground font-medium flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Pulizie
            </h2>
            
            {attivitaGiorno.pulizie?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground bg-card rounded-2xl border border-border">
                <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessuna pulizia programmata per oggi</p>
              </div>
            ) : (
              attivitaGiorno.pulizie?.map((task) => (
                <button 
                  key={task.id}
                  onClick={() => canInteract && handleCheckPulizia(task)}
                  disabled={!canInteract}
                  className={`w-full p-4 rounded-2xl border transition-all shadow-sm text-left ${
                    !canInteract ? "opacity-75 cursor-default" :
                    "active:scale-[0.98]"
                  } ${
                    task.completata 
                      ? "bg-primary/10 border-primary/40" 
                      : "bg-card border-border hover:border-primary/30 hover:bg-primary/5"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-all border-2 ${
                        task.completata 
                          ? "bg-primary border-primary text-white" 
                          : "bg-card border-border"
                      }`}
                    >
                      {task.completata && <Check className="h-4 w-4" />}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: task.area_colore }}
                        />
                        <span className="text-xs text-foreground/60">{task.area_nome}</span>
                      </div>
                      <h4 className={`font-medium mt-1 ${task.completata ? "text-primary" : "text-foreground"}`}>
                        {task.nome}
                      </h4>
                      {task.descrizione && (
                        <p className="text-xs text-foreground/50 mt-1 line-clamp-2">{task.descrizione}</p>
                      )}
                      {task.completata && task.operatore_nome && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-primary/70">
                          <User className="h-3 w-3" />
                          <span>{task.operatore_nome}</span>
                          {task.ora_completamento && (
                            <>
                              <Clock className="h-3 w-3 ml-2" />
                              <span>{task.ora_completamento}</span>
                            </>
                          )}
                        </div>
                      )}
                      {!task.completata && canInteract && (
                        <p className="text-xs text-primary font-medium mt-2">Tocca per completare</p>
                      )}
                      {!canInteract && !task.completata && (
                        <p className="text-xs text-foreground/50 mt-2">Non disponibile</p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Preparazioni Section */}
          {((attivitaGiorno.preparazioni?.length || 0) > 0 || (attivitaGiorno.preparazioni_spontanee?.length || 0) > 0) && (
            <div className="space-y-3" data-testid="preparazioni-section">
              <h2 className="text-foreground font-medium flex items-center gap-2">
                <ChefHat className="h-4 w-4 text-teal-500" />
                Preparazioni da fare oggi
                {attivitaGiorno.stats?.preparazioni_totali > 0 && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({attivitaGiorno.stats?.preparazioni_completate || 0}/{attivitaGiorno.stats?.preparazioni_totali || 0})
                  </span>
                )}
              </h2>

              {(attivitaGiorno.preparazioni || []).map((task) => {
                const stato = task.stato_preparazione || "non_fatta";
                const target = task.quantita_target || 0;
                const qp = task.quantita_preparata || 0;
                const udm = task.unita_misura || "";
                const taskColor = task.colore || "#14b8a6";
                const statoBadgeClass =
                  stato === "completa" ? "bg-emerald-100 text-emerald-700 border-emerald-300" :
                  stato === "parziale" ? "bg-amber-100 text-amber-700 border-amber-300" :
                  "bg-card border-border";
                const statoLabel = stato === "completa" ? "Completa" : stato === "parziale" ? `Parziale ${qp}/${target}` : "Da fare";
                return (
                  <button
                    key={task.id}
                    onClick={() => canInteract && openPrepDialog(task)}
                    disabled={!canInteract}
                    className={`w-full p-4 rounded-2xl border transition-all shadow-sm text-left ${
                      canInteract ? "active:scale-[0.98] cursor-pointer" : "cursor-default opacity-75"
                    } ${
                      stato === "completa" ? "bg-teal-50 border-teal-300" :
                      stato === "parziale" ? "bg-amber-50 border-amber-300" :
                      "bg-card border-border hover:border-teal-300"
                    }`}
                    data-testid={`prep-task-${task.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: stato === "non_fatta" ? "transparent" : taskColor, borderColor: taskColor, borderWidth: 2 }}
                      >
                        {stato === "completa" && <Check className="h-4 w-4 text-white" />}
                        {stato === "parziale" && <span className="text-white text-xs font-bold">½</span>}
                        {stato === "non_fatta" && <ChefHat className="h-4 w-4 text-teal-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded border ${statoBadgeClass}`}>
                            {statoLabel}
                          </span>
                          <span className="text-xs text-foreground/60">
                            Target {target} {udm}
                          </span>
                          {task.fonte === "lotto" && (
                            <span className="text-xs px-1.5 py-0.5 rounded border border-teal-300 bg-teal-50 text-teal-700">
                              da lotto
                            </span>
                          )}
                          {task.fonte === "manuale" && (
                            <span className="text-xs px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">
                              manuale
                            </span>
                          )}
                          {task.fonte === "manuale_override" && (
                            <span className="text-xs px-1.5 py-0.5 rounded border border-orange-300 bg-orange-50 text-orange-700">
                              override
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium mt-1 text-foreground">
                          {task.ricetta_nome || task.nome}
                        </h4>
                        {task.descrizione && (
                          <p className="text-xs text-foreground/50 mt-1 line-clamp-2">{task.descrizione}</p>
                        )}
                        {qp > 0 && task.operatore_nome && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-foreground/60">
                            <User className="h-3 w-3" />
                            <span>{task.operatore_nome}</span>
                            {task.ora_completamento && (
                              <>
                                <Clock className="h-3 w-3 ml-2" />
                                <span>{task.ora_completamento}</span>
                              </>
                            )}
                          </div>
                        )}
                        {canInteract && stato !== "completa" && (
                          <p className="text-xs text-teal-600 font-medium mt-2">Tocca per registrare la quantità</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Preparazioni spontanee */}
              {(attivitaGiorno.preparazioni_spontanee || []).length > 0 && (
                <div className="pt-2">
                  <p className="text-xs uppercase tracking-wide text-foreground/50 mb-2">Altre preparazioni eseguite oggi</p>
                  {attivitaGiorno.preparazioni_spontanee.map((p) => (
                    <div
                      key={p.lotto_id}
                      className="w-full p-3 rounded-xl border border-border bg-card/60 mb-2 flex items-center gap-3"
                      data-testid={`prep-spontanea-${p.lotto_id}`}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-100">
                        <ChefHat className="h-4 w-4 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-foreground">{p.ricetta_nome}</p>
                        <p className="text-xs text-foreground/60">
                          {p.quantita_preparata ? `${p.quantita_preparata} ${p.unita_misura || ""}` : (p.quantita_testo || "")}
                          {p.operatore && ` · ${p.operatore}`}
                          {p.ora && ` · ${p.ora}`}
                        </p>
                      </div>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
                        Spontanea
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Task Gestione Section */}
          {(attivitaGiorno.gestione?.length > 0 || true) && (
            <div className="space-y-3">
              <h2 className="text-foreground font-medium flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-orange-500" />
                Task Gestione
              </h2>
              
              {attivitaGiorno.gestione?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground bg-card rounded-2xl border border-border">
                  <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nessun task di gestione per oggi</p>
                </div>
              ) : (
                attivitaGiorno.gestione?.map((task) => {
                  const isOperativa = task.tipo === "operativa";
                  const TaskIcon = isOperativa ? CheckCircle2 : Info;
                  const taskColor = task.colore || "#f97316";
                  
                  return (
                    <button 
                      key={task.id}
                      onClick={() => isOperativa && canInteract && handleCheckGestione(task)}
                      disabled={!isOperativa || !canInteract}
                      className={`w-full p-4 rounded-2xl border transition-all shadow-sm text-left ${
                        (isOperativa && canInteract) ? "active:scale-[0.98] cursor-pointer" : "cursor-default"
                      } ${
                        !canInteract ? "opacity-75" : ""
                      } ${
                        task.completata 
                          ? "bg-orange-50 border-orange-300" 
                          : isOperativa 
                            ? "bg-card border-border hover:border-orange-300 hover:bg-orange-50/50"
                            : "bg-card border-border"
                      }`}
                      data-testid={`gestione-task-${task.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox solo per task operative */}
                        {isOperativa ? (
                          <div
                            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-all border-2 ${
                              task.completata 
                                ? "text-white border-orange-500" 
                                : "bg-card border-border"
                            }`}
                            style={task.completata ? { backgroundColor: taskColor, borderColor: taskColor } : {}}
                          >
                            {task.completata && <Check className="h-4 w-4" />}
                          </div>
                        ) : (
                          <div 
                            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 bg-purple-100"
                          >
                            <Info className="h-4 w-4 text-purple-600" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: taskColor }}
                            />
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              isOperativa 
                                ? "bg-orange-100 text-orange-700" 
                                : "bg-purple-100 text-purple-700"
                            }`}>
                              {isOperativa ? "Operativa" : "Info"}
                            </span>
                          </div>
                          <h4 className={`font-medium mt-1 ${
                            task.completata 
                              ? "text-orange-700" 
                              : "text-foreground"
                          }`}>
                            {task.nome}
                          </h4>
                          {task.descrizione && (
                            <p className="text-xs text-foreground/50 mt-1 line-clamp-2">{task.descrizione}</p>
                          )}
                          {task.completata && task.operatore_nome && (
                            <div className="flex items-center gap-2 mt-2 text-xs text-orange-600/70">
                              <User className="h-3 w-3" />
                              <span>{task.operatore_nome}</span>
                              {task.ora_completamento && (
                                <>
                                  <Clock className="h-3 w-3 ml-2" />
                                  <span>{task.ora_completamento}</span>
                                </>
                              )}
                            </div>
                          )}
                          {isOperativa && !task.completata && canInteract && (
                            <p className="text-xs text-orange-600 font-medium mt-2">Tocca per completare</p>
                          )}
                          {isOperativa && !task.completata && !canInteract && (
                            <p className="text-xs text-foreground/50 mt-2">Non disponibile</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* Temperature Section */}
          <div className="space-y-3">
            <h2 className="text-foreground font-medium flex items-center gap-2">
              <Thermometer className="h-4 w-4 text-blue-500" />
              Controllo Temperature
            </h2>
            
            {attivitaGiorno.temperature?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground bg-card rounded-2xl border border-border">
                <Thermometer className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nessun frigorifero configurato</p>
              </div>
            ) : (
              attivitaGiorno.temperature?.map((frigo) => (
                <button
                  key={frigo.id}
                  onClick={() => canInteract && openTempDialog(frigo)}
                  disabled={!canInteract}
                  className={`w-full p-4 rounded-2xl border text-left transition-all shadow-sm ${
                    !canInteract ? "opacity-75 cursor-default" : ""
                  } ${
                    frigo.completata 
                      ? frigo.is_anomala 
                        ? "bg-red-50 border-red-300"
                        : "bg-blue-50 border-blue-300" 
                      : "bg-card border-border hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        frigo.completata 
                          ? frigo.is_anomala ? "bg-red-100" : "bg-blue-100"
                          : "bg-muted"
                      }`}>
                        <Thermometer className={`h-5 w-5 ${
                          frigo.completata 
                            ? frigo.is_anomala ? "text-red-600" : "text-blue-600"
                            : "text-muted-foreground"
                        }`} />
                      </div>
                      <div>
                        <h4 className="text-foreground font-medium">{frigo.nome}</h4>
                        <p className="text-xs text-foreground/50">{frigo.tipologia}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      {frigo.completata ? (
                        <>
                          <p className={`text-xl font-bold ${
                            frigo.is_anomala ? "text-red-600" : "text-blue-600"
                          }`}>
                            {frigo.temperatura}°C
                          </p>
                          <p className="text-xs text-foreground/50">{frigo.ora_registrazione}</p>
                        </>
                      ) : (
                        <span className="text-foreground/50 text-sm">Da registrare</span>
                      )}
                    </div>
                  </div>
                  
                  {frigo.is_anomala && (
                    <div className="flex items-center gap-2 mt-3 p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-xs text-red-700">
                        Fuori range ({frigo.temperatura_min}°C - {frigo.temperatura_max}°C)
                      </span>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Vista Settimana */}
      {viewMode === "settimana" && (
        <div className="space-y-4">
          {weekData.map((dayData, idx) => {
            const date = new Date(dayData.day);
            const isCurrentDay = isToday(date);
            // Includi anche le task di gestione nel conteggio totale
            const totalTasks = (dayData.stats?.pulizie_totali || 0) + (dayData.stats?.gestione_totali || 0) + (dayData.stats?.temperature_totali || 0) + (dayData.stats?.preparazioni_totali || 0);
            const completedTasks = (dayData.stats?.pulizie_completate || 0) + (dayData.stats?.gestione_completate || 0) + (dayData.stats?.temperature_completate || 0) + (dayData.stats?.preparazioni_completate || 0);
            const completionPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
            
            return (
              <button
                key={dayData.date}
                onClick={() => {
                  setCurrentDate(new Date(dayData.day));
                  setViewMode("giorno");
                }}
                className={`w-full p-4 rounded-2xl border text-left transition-all shadow-sm ${
                  isCurrentDay 
                    ? "bg-primary/5 border-primary/30" 
                    : "bg-card border-border hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isCurrentDay ? "bg-primary text-white" : "bg-muted"
                    }`}>
                      <span className={`font-bold ${isCurrentDay ? "" : "text-foreground"}`}>
                        {date.getDate()}
                      </span>
                    </div>
                    <div>
                      <p className={`font-medium ${isCurrentDay ? "text-primary" : "text-foreground"}`}>
                        {GIORNI_SETTIMANA[idx]}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {dayData.stats?.pulizie_totali || 0} pulizie, {dayData.stats?.temperature_totali || 0} temperature
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      completionPercent === 100 ? "text-green-600" : 
                      completionPercent > 0 ? "text-amber-600" : "text-muted-foreground"
                    }`}>
                      {completedTasks}/{totalTasks}
                    </p>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      completionPercent === 100 ? "bg-green-500" : "bg-primary"
                    }`}
                    style={{ width: `${completionPercent}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Dialog Temperatura */}
      <Dialog open={tempDialogOpen} onOpenChange={setTempDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Thermometer className="h-5 w-5 text-blue-500" />
              {selectedFrigo?.nome}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleTempSubmit} className="space-y-4">
            <div className="p-3 bg-muted rounded-xl text-sm">
              <p className="text-muted-foreground">Range accettabile:</p>
              <p className="text-foreground font-medium">
                {selectedFrigo?.temperatura_min}°C - {selectedFrigo?.temperatura_max}°C
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Temperatura rilevata (°C) *</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  value={tempForm.temperatura}
                  onChange={(e) => setTempForm({ temperatura: e.target.value })}
                  placeholder="es. 4.5"
                  className="bg-muted border-border text-foreground text-lg font-bold"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleOcrTemp}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={ocrLoading}
                  className="border-blue-400 text-blue-600 hover:bg-blue-50"
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
              {ocrLoading && <p className="text-xs text-blue-600">Analisi foto in corso...</p>}
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setTempDialogOpen(false)} className="flex-1 border-border text-foreground hover:bg-muted">
                Annulla
              </Button>
              <Button type="submit" disabled={tempSaving} className="flex-1 bg-blue-500 hover:bg-blue-400 text-white font-bold">
                {tempSaving ? "Salvataggio..." : "Registra"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Preparazione */}
      <Dialog open={prepDialogOpen} onOpenChange={setPrepDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-teal-500" />
              {selectedPrep?.ricetta_nome || selectedPrep?.nome}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handlePrepSubmit} className="space-y-4">
            <div className="p-3 bg-teal-50 dark:bg-teal-500/10 rounded-xl text-sm border border-teal-200 dark:border-teal-500/30">
              <p className="text-muted-foreground">Target previsto:</p>
              <p className="text-teal-700 dark:text-teal-400 font-bold text-lg">
                {selectedPrep?.quantita_target} {selectedPrep?.unita_misura}
              </p>
              {selectedPrep?.quantita_lotti > 0 && (
                <p className="text-xs text-foreground/70 mt-1">
                  Lotti già registrati oggi: <strong>{selectedPrep.quantita_lotti} {selectedPrep.unita_misura}</strong>
                  {selectedPrep?.operatori_contribuenti?.length > 0 && (
                    <span className="ml-1 text-foreground/50">
                      ({selectedPrep.operatori_contribuenti.join(" + ")})
                    </span>
                  )}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Quantità realmente preparata *</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={prepQuantita}
                  onChange={(e) => setPrepQuantita(e.target.value)}
                  placeholder="es. 10"
                  className="bg-muted border-border text-foreground text-lg font-bold flex-1"
                  data-testid="prep-quantita-input"
                  autoFocus
                />
                <span className="text-foreground/70 text-sm">{selectedPrep?.unita_misura}</span>
              </div>
              <p className="text-xs text-foreground/50">
                Inserisci 0 per indicare &laquo;non preparata&raquo;. Inserisci meno del target per &laquo;parziale&raquo;.
              </p>
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPrepDialogOpen(false)} className="flex-1 border-border text-foreground hover:bg-muted">
                Annulla
              </Button>
              <Button type="submit" disabled={prepSaving} className="flex-1 bg-teal-500 hover:bg-teal-400 text-white font-bold" data-testid="prep-submit-btn">
                {prepSaving ? "Salvataggio..." : "Registra"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarioAttivita;
